import React, { useCallback } from 'react';
import { ParamListBase, RouteProp } from '@react-navigation/core';
import { partnerSupplier } from '@/utils/environment';
import {
  Platform,
  StyleSheet,
  TouchableWithoutFeedback,
  View,
} from 'react-native';
import {
  StackNavigationOptions,
  StackNavigationProp,
  TransitionPresets,
} from '@react-navigation/stack';
import { Theme, useTheme } from '@/contexts/theme-context';
import { useLayoutContext } from '@/contexts/layout-context';

interface Options<ParamList extends ParamListBase> {
  modalTransitions?: boolean;
  onOverlayPress?: (navigation: StackNavigationProp<ParamList>) => void;
}

export const useModalStackScreenOptions = <ParamList extends ParamListBase>({
  modalTransitions = true,
  onOverlayPress,
}: Options<ParamList> = {}) => {
  const theme = useTheme();
  const { isSmallLayout } = useLayoutContext();
  return useCallback(
    ({
      navigation,
      route,
    }: {
      navigation: StackNavigationProp<ParamList>;
      route: RouteProp<ParamList, keyof ParamList>;
    }): StackNavigationOptions => {
      const options: StackNavigationOptions = {
        headerShown: false,
        presentation: 'modal',
        title: partnerSupplier.fullName,
      };

      if (navigation.getState().routes[0]?.name === route.name) {
        // Do not include modal options for the initial route
        return options;
      }

      return modalTransitions
        ? {
            ...options,
            ...modalTransitionOptions(
              isSmallLayout,
              navigation,
              theme,
              onOverlayPress,
            ),
          }
        : options;
    },
    [isSmallLayout, modalTransitions, theme, onOverlayPress],
  );
};

const modalTransitionOptions = <ParamList extends ParamListBase>(
  isSmallLayout: boolean,
  navigation: StackNavigationProp<ParamList>,
  theme: Theme,
  onOverlayPress?: (navigation: StackNavigationProp<ParamList>) => void,
): StackNavigationOptions => {
  if (isSmallLayout) {
    return Platform.select({
      default: {
        ...TransitionPresets.ModalTransition,
      },
      ios: {
        ...TransitionPresets.ModalPresentationIOS,
        cardOverlayEnabled: true,
      },
    });
  }

  return {
    cardOverlay: (props) => (
      <TouchableWithoutFeedback
        onPress={() =>
          onOverlayPress ? onOverlayPress(navigation) : navigation.popToTop()
        }
      >
        <ModalCardOverlay {...props} />
      </TouchableWithoutFeedback>
    ),
    cardOverlayEnabled: true,
    cardStyle: [
      styles.modalCard,
      Platform.OS !== 'web' ? styles.modalCardMaxHeight : null,
    ],
    cardStyleInterpolator: ({ next }) => ({
      cardStyle: next ? {} : styles.modalCardNext,
      containerStyle: next
        ? {}
        : [
            styles.modalCardContainerNext,
            { backgroundColor: theme.colors.transparent },
          ],
    }),
    detachPreviousScreen: false,
  };
};

export const ModalCardOverlay: React.FC<
  Parameters<NonNullable<StackNavigationOptions['cardOverlay']>>[0]
> = (props) => {
  const theme = useTheme();
  return (
    <View
      {...props}
      style={[styles.modalCardOverlay, { backgroundColor: theme.colors.black }]}
    />
  );
};

const styles = StyleSheet.create({
  flexModalCard: {
    flexGrow: 1,
  },
  modalCard: {
    borderRadius: 16,
    // These properties set the modal height to fit the modal content
    // on web but doesn't work on native
    ...(Platform.OS === 'web'
      ? { flexBasis: 'auto', flexGrow: 0 }
      : { flexGrow: 1 }),
  },
  modalCardContainerNext: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    paddingVertical: 40,
  },
  modalCardMaxHeight: {
    maxHeight: '70%',
  },
  modalCardNext: {
    justifyContent: 'center',
    maxWidth: 640,
    width: '100%',
    // Declaring this property on mobile breaks the app
    ...(Platform.OS === 'web' ? { pointerEvents: 'none' } : {}),
  },
  modalCardOverlay: {
    ...StyleSheet.absoluteFillObject,
    opacity: 0.7,
  },
});

export const flexModalCardStyle = [styles.modalCard, styles.flexModalCard];
